// Core
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import { Chip, FieldSearchSelect, FieldSelect, RemoteTable } from "components/AtomsV2";

// Redux
import { useSelector } from "react-redux";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useGetOpportunityCostsByCommunityIdPaginationQuery } from "state/opportunity_costs/opportunityCostsApiSlice";

// Utils
import { useGetMeters } from "utils/crud/read";
import { datetimeToDate, datetimeToTime } from "utils/time";

const value4 = (color) => (v) =>
  (
    <span className="tabular-nums" style={{ color, fontWeight: 600 }}>
      {v != null ? Number(v).toFixed(4) : "—"}
    </span>
  );

function ListOpportunityCosts({ setIsLoading }) {
  const community = useSelector(selectCurrentCommunity);
  const meters = useGetMeters("true");
  const { t } = useTranslation(["opportunity_costs"]);

  const [page, setPage] = useState(0);
  const [limit] = useState(10);
  const [sortOrder, setSortOrder] = useState(undefined);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [meter, setMeter] = useState(null);
  const [isUpload, setIsUpload] = useState(null);

  // READ Opportunity Costs (server-side pagination / filtering / sorting)
  const { data, isFetching, isLoading } = useGetOpportunityCostsByCommunityIdPaginationQuery(
    {
      page,
      limit,
      date,
      time,
      isUpload,
      meterId: meter,
      communityId: community.id,
      sortColumn: sortOrder?.column,
      sortDirection: sortOrder?.direction,
    },
    {
      pollingInterval: 15000,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    setIsLoading(isLoading || isFetching);
  }, [isLoading, isFetching]);

  const meterIdToIdentifier = (meterId) => meters?.find((m) => m.id === meterId)?.identifier ?? "—";

  const rows = useMemo(
    () =>
      (data?.data ?? []).map((row, i) => ({
        id: `${row.datetime_timestamp}-${row.meter_id}-${i}`,
        date: datetimeToDate(row.datetime_timestamp),
        time: datetimeToTime(row.datetime_timestamp),
        meter_id: row.meter_id ? meterIdToIdentifier(row.meter_id) : "—",
        l_buy: row.l_buy,
        l_sell: row.l_sell,
        is_upload: row.is_upload,
      })),
    [data, meters]
  );

  const columns = [
    { key: "date", label: t("table.columns.date"), sortable: true, width: "14%" },
    { key: "time", label: t("table.columns.time"), sortable: true, width: "12%" },
    {
      key: "meter_id",
      label: t("table.columns.meter"),
      sortable: true,
      width: "24%",
      render: (v) => <span className="font-mono text-[12.5px] text-ink">{v}</span>,
    },
    {
      key: "l_buy",
      label: t("table.columns.l_buy"),
      align: "right",
      render: value4("var(--accent)"),
    },
    {
      key: "l_sell",
      label: t("table.columns.l_sell"),
      align: "right",
      render: value4("var(--warm)"),
    },
    {
      key: "is_upload",
      label: t("table.columns.type"),
      render: (v) =>
        v ? (
          <Chip tone="gray">{t("table.type.user")}</Chip>
        ) : (
          <Chip tone="teal">{t("table.type.platform")}</Chip>
        ),
    },
  ];

  const allLabel = t("filters.all", { defaultValue: "Todos", ns: "common" });

  const dateOptions = [
    { value: "", label: allLabel },
    ...(data?.distinct_dates ?? []).map((o) => ({ value: o.date, label: o.date })),
  ];
  const timeOptions = [
    { value: "", label: allLabel },
    ...(data?.distinct_times ?? []).map((o) => ({ value: o.time, label: o.time })),
  ];
  const meterOptions = [
    { value: "", label: allLabel },
    ...(meters ?? []).map((m) => ({ value: m.identifier, label: m.identifier })),
  ];
  const typeOptions = [
    { value: "", label: allLabel },
    { value: "false", label: t("table.type.calculated_by_platform") },
    { value: "true", label: t("table.type.uploaded_by_user") },
  ];

  const resetPage = () => setPage(0);
  const onDate = (v) => {
    resetPage();
    setDate(v || null);
  };
  const onTime = (v) => {
    resetPage();
    setTime(v || null);
  };
  const onMeter = (identifier) => {
    resetPage();
    setMeter(meters?.find((m) => m.identifier === identifier)?.id ?? null);
  };
  const onType = (v) => {
    resetPage();
    setIsUpload(v || null);
  };

  const filters = (
    <>
      <FieldSearchSelect
        label={t("table.columns.date")}
        value={date ?? ""}
        onChange={onDate}
        options={dateOptions}
      />
      <FieldSearchSelect
        label={t("table.columns.time")}
        value={time ?? ""}
        onChange={onTime}
        options={timeOptions}
      />
      <FieldSearchSelect
        label={t("table.columns.meter")}
        value={meters?.find((m) => m.id === meter)?.identifier ?? ""}
        onChange={onMeter}
        options={meterOptions}
      />
      <FieldSelect
        label={t("table.columns.type")}
        value={isUpload ?? ""}
        onChange={onType}
        options={typeOptions}
      />
    </>
  );

  return (
    <div className="rec-card">
      <RemoteTable
        columns={columns}
        rows={rows}
        totalCount={data?.total_count ?? 0}
        page={page}
        limit={limit}
        onPageChange={setPage}
        sort={sortOrder}
        onSortChange={setSortOrder}
        filters={filters}
        loading={isFetching}
        emptyText={t("charts.no_data")}
      />
    </div>
  );
}

ListOpportunityCosts.defaultProps = {
  setIsLoading: () => {},
};

ListOpportunityCosts.propTypes = {
  setIsLoading: PropTypes.func,
};

export default ListOpportunityCosts;
